/**
 * Utility Functions
 */

import dayjs from 'dayjs';
import { colorPalette } from '../theme';

/**
 * Safely get a nested property from an object
 */
export const safeGet = (path, obj, defaultValue = null) => {
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current === null || current === undefined) {
      return defaultValue;
    }
    current = current[key];
  }
  
  return current ?? defaultValue;
};

/**
 * Format a date/timestamp for display
 */
export const formatDateTime = (timestamp, format = 'M/D/YYYY h:mm A') => {
  if (!timestamp) return '';
  
  // Handle Unix timestamp (seconds or milliseconds)
  const ms = typeof timestamp === 'number' && timestamp < 10000000000
    ? timestamp * 1000
    : timestamp;
  
  return dayjs(ms).format(format);
};

/**
 * Format a date only
 */
export const formatDate = (timestamp, format = 'M/D/YYYY') => {
  return formatDateTime(timestamp, format);
};

/**
 * Get relative time (e.g., "2 hours ago")
 */
export const getRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  
  const ms = typeof timestamp === 'number' && timestamp < 10000000000
    ? timestamp * 1000
    : timestamp;
  
  return dayjs(ms).fromNow();
};

/**
 * Check if an asset is connected (received data recently)
 */
export const isAssetConnected = (asset, thresholdMinutes = 60) => {
  if (!asset?.receivedTs) return false;
  
  const receivedMs = typeof asset.receivedTs === 'number' && asset.receivedTs < 10000000000
    ? asset.receivedTs * 1000
    : asset.receivedTs;
  
  const threshold = thresholdMinutes * 60 * 1000;
  return Date.now() - receivedMs < threshold;
};

/**
 * Get status color for asset status
 */
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'moving':
      return '#4CAF50'; // Green
    case 'idle':
      return '#FFC107'; // Amber
    case 'offline':
      return '#9E9E9E'; // Gray
    default:
      return colorPalette.asseTracOrange;
  }
};

/**
 * Filter a list by multiple properties
 */
export const filterBy = ({ list, filters, props, override }) => {
  if (!filters || filters.length === 0) return list;
  
  return list.filter((item) => {
    // Check override function first
    if (override && override(item)) {
      return true;
    }
    
    // Check each property
    return props.some((prop) => {
      const value = safeGet(prop, item);
      return filters.includes(value);
    });
  });
};

/**
 * Generate a unique ID
 */
export const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Debounce a function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle a function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const toRad = (deg) => deg * (Math.PI / 180);

/**
 * Format a number with commas
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Truncate text with ellipsis
 */
export const truncate = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Deep clone an object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Check if two arrays have any common elements
 */
export const hasCommonElements = (arr1, arr2) => {
  return arr1.some((item) => arr2.includes(item));
};

export default {
  safeGet,
  formatDateTime,
  formatDate,
  getRelativeTime,
  isAssetConnected,
  getStatusColor,
  filterBy,
  generateId,
  debounce,
  throttle,
  calculateDistance,
  formatNumber,
  truncate,
  deepClone,
  hasCommonElements,
};
